import { useMemo } from 'react';
import { useListTenantCollectionTicketClaims } from '@/api/trafikportalen';
import { STUDENT_COLLECTION_TYPES } from '@/helpers/collectionTypes';
import { countOrganizationsMissingCurrentPeriod } from '@/helpers/periodStatus';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';
import type { StudentCollectionType } from '@/helpers/collectionTypes';

const emptyCounts = (): Record<StudentCollectionType, number> => ({
  'school-transportation': 0,
  'school-student-travel': 0,
  'school-child-and-youth': 0,
});

export const useOrganizationsMissingCurrentPeriod = (
  organizations: OrganizationDTO[] | undefined,
) => {
  const travel = useListTenantCollectionTicketClaims({
    collection_type: ['school-student-travel'],
  });
  const transportation = useListTenantCollectionTicketClaims({
    collection_type: ['school-transportation'],
  });
  const youth = useListTenantCollectionTicketClaims({
    collection_type: ['school-child-and-youth'],
  });

  const claims: Record<
    StudentCollectionType,
    CollectionTicketClaimDTO[] | undefined
  > = {
    'school-student-travel': travel.data,
    'school-transportation': transportation.data,
    'school-child-and-youth': youth.data,
  };

  const isLoading: Record<StudentCollectionType, boolean> = {
    'school-student-travel': travel.isLoading,
    'school-transportation': transportation.isLoading,
    'school-child-and-youth': youth.isLoading,
  };

  const isError: Record<StudentCollectionType, boolean> = {
    'school-student-travel': travel.isError,
    'school-transportation': transportation.isError,
    'school-child-and-youth': youth.isError,
  };

  const counts = useMemo(() => {
    const next = emptyCounts();
    if (!organizations) return next;

    const claimsByType: Record<
      StudentCollectionType,
      CollectionTicketClaimDTO[] | undefined
    > = {
      'school-student-travel': travel.data,
      'school-transportation': transportation.data,
      'school-child-and-youth': youth.data,
    };

    for (const collectionType of STUDENT_COLLECTION_TYPES) {
      const categoryClaims = claimsByType[collectionType];
      if (!categoryClaims) continue;
      next[collectionType] = countOrganizationsMissingCurrentPeriod(
        organizations,
        categoryClaims,
        collectionType,
      );
    }

    return next;
  }, [organizations, travel.data, transportation.data, youth.data]);

  const total = useMemo(
    () =>
      STUDENT_COLLECTION_TYPES.reduce(
        (sum, collectionType) => sum + counts[collectionType],
        0,
      ),
    [counts],
  );

  return { counts, total, claims, isLoading, isError };
};
